import { 
    collection, 
    getDocs,
    getDoc,
    deleteDoc, 
    doc,
    updateDoc, 
    query, 
    where, 
    collectionGroup,
    writeBatch,
    deleteField,
    serverTimestamp
} from "firebase/firestore";
import { db, auth } from "./config";
import { saveToFirestore } from './cleanPayload';

/**
 * 1. Función Auxiliar: Limpieza por Lotes (Batch)
 * Borra documentos de una colección o consulta en una sola petición (máx 500).
 */
export const deleteCollectionInBatch = async (collectionOrQuery) => {
    try {
        const snapshot = await getDocs(collectionOrQuery);
        if (snapshot.empty) return;

        const batch = writeBatch(db);
        snapshot.docs.forEach((d) => batch.delete(d.ref));
        await batch.commit();
    } catch (error) {
        console.error("Error en deleteCollectionInBatch:", error);
    }
};

/**
 * 2. Borrado de Liquidaciones por Número de Póliza
 */
export const deleteLiquidacionesByPoliza = async (nPoliza) => {
    if (!nPoliza) return;
    try {
        const q = query(collection(db, 'public/data/liquidaciones'), where('nPoliza', '==', nPoliza));
        await deleteCollectionInBatch(q);
    } catch (error) {
        console.error("Error eliminando liquidaciones vinculadas:", error);
    }
};

/**
 * 3. Borrado Profundo de un Siniestro (Incluye historial de gestiones)
 */
export const deleteSiniestroFull = async (parentPath, siniestroId) => {
    try {
        const pathCompleto = `${parentPath}/siniestros/${siniestroId}`;
        await deleteCollectionInBatch(collection(db, pathCompleto, 'gestiones'));
        await deleteDoc(doc(db, `${parentPath}/siniestros`, siniestroId));
    } catch (error) {
        console.error("Error en deleteSiniestroFull:", error);
    }
};

/**
 * 4. Borrado Profundo de Cobranza (Incluye historial de observaciones)
 */
export const deleteCobranzaFull = async (parentPath, cobranzaId) => {
    try {
        const pathCompleto = `${parentPath}/cobranza/${cobranzaId}`;
        await deleteCollectionInBatch(collection(db, pathCompleto, 'observaciones'));
        await deleteDoc(doc(db, `${parentPath}/cobranza`, cobranzaId));
    } catch (error) {
        console.error("Error en deleteCobranzaFull:", error);
    }
};

    /**
     * 5. Borrado de Endoso ART con limpieza de Cotización vinculada
     */
    export const deleteEndosoArtFull = async (endoso) => {
        if (!endoso || !endoso.path) return;
        try {
            const batch = writeBatch(db);
            batch.delete(doc(db, endoso.path));

            if (endoso.cotizacionVinculadaId && (endoso.clientId || endoso.entidadId)) {
                const clienteId = endoso.clientId || endoso.entidadId;
                const quoteRef = doc(db, `public/data/clientes/${clienteId}/cotizaciones_art`, endoso.cotizacionVinculadaId);
                batch.delete(quoteRef);
            }

            await batch.commit();
        } catch (error) {
            console.error("Error en deleteEndosoArtFull:", error);
            throw error;
        }
    };

/**
 * 6. Borrado Profundo de Póliza (Cascada total optimizada)
 */
export const deletePolizaFull = async (clientId, polizaId, nPoliza = null) => {
    const polizaRefPath = `public/data/clientes/${clientId}/polizas/${polizaId}`;
    try {
        if (nPoliza) await deleteLiquidacionesByPoliza(nPoliza);

        const cobranzaSnap = await getDocs(collection(db, polizaRefPath, 'cobranza'));
        for (const cDoc of cobranzaSnap.docs) await deleteCobranzaFull(polizaRefPath, cDoc.id);

        const siniestrosSnap = await getDocs(collection(db, polizaRefPath, 'siniestros'));
        for (const sDoc of siniestrosSnap.docs) await deleteSiniestroFull(polizaRefPath, sDoc.id);

        const simpleCols = ['endosos', 'refacturaciones', 'deuda', 'gestiones_postventa'];
        for (const col of simpleCols) {
            await deleteCollectionInBatch(collection(db, polizaRefPath, col));
        }

        await deleteDoc(doc(db, `public/data/clientes/${clientId}/polizas`, polizaId));
    } catch (error) {
        console.error("Error en deletePolizaFull:", error);
    }
};

/**
 * 7. Borrado Profundo de Contrato ART (FIXED: Ahora limpia cotizaciones vinculadas)
 */
export const deleteContractFull = async (clientId, contratoId) => {
    const contractRefPath = `public/data/clientes/${clientId}/contratos/${contratoId}`;
    try {
        const batch = writeBatch(db);

        // A. Limpiar cotizaciones vinculadas en la tabla general de cotizaciones_art del cliente
        // Buscamos cualquier cotización que apunte a este contrato para evitar datos huérfanos
        const quotesRef = collection(db, `public/data/clientes/${clientId}/cotizaciones_art`);
        const quotesSnap = await getDocs(quotesRef);
        
        quotesSnap.docs.forEach((qDoc) => {
            const data = qDoc.data();
            if (data.contratoVinculadoId === contratoId || data.contratoOrigenId === contratoId) {
                batch.delete(qDoc.ref);
            }
        });

        // B. Borrar Cobranzas profundas vinculadas al contrato
        const cobranzaSnap = await getDocs(collection(db, contractRefPath, 'cobranza'));
        for (const cDoc of cobranzaSnap.docs) {
            await deleteCobranzaFull(contractRefPath, cDoc.id);
        }

        // C. Borrar subcolecciones internas con el helper batch
        await deleteCollectionInBatch(collection(db, contractRefPath, 'cotizaciones'));
        await deleteCollectionInBatch(collection(db, contractRefPath, 'aumentos_art'));

        // D. Borrar el documento del contrato
        batch.delete(doc(db, `public/data/clientes/${clientId}/contratos`, contratoId));

        await batch.commit();
    } catch (error) {
        console.error("Error en deleteContractFull:", error);
        throw error;
    }
};

/**
 * 8. Borrado Profundo de Remesa de Deuda
 */
export const deleteRemesaFull = async (batchId, remesaDocId) => {
    try {
        // 1. Obtener el registro de la remesa para leer los documentos creados originalmente
        const remesaRef = doc(db, 'public/data/remesas_deuda', remesaDocId);
        const remesaSnap = await getDoc(remesaRef);

        if (!remesaSnap.exists()) {
            throw new Error("El lote de remesa seleccionado no existe en el sistema.");
        }

        const remesaData = remesaSnap.data();
        const paths = remesaData.documentosCreados || [];

        // Set para coleccionar las pólizas de forma única para su posterior re-auditoría
        const polizasAfectadas = new Set(); // Almacenará: public/data/clientes/{clientId}/polizas/{polizaId}

        // 2. Barrido y borrado profundo físico por ID directo
        for (const path of paths) {
            const parts = path.split('/');
            // public/data/clientes/{clientId}/polizas/{polizaId}/cobranza/{cobranzaId}
            if (parts.length >= 8) {
                const clientId = parts[3];
                const polizaId = parts[5];
                const cobranzaId = parts[7];

                const parentPath = `public/data/clientes/${clientId}/polizas/${polizaId}`;
                polizasAfectadas.add(parentPath);

                // Borramos observaciones de bitácora y el hito de cobranza usando la utilidad atómica
                await deleteCobranzaFull(parentPath, cobranzaId);
            }
        }

        // 3. Re-auditar las pólizas afectadas para apagar o mantener tieneDeuda
        for (const polizaPath of Array.from(polizasAfectadas)) {
            const cobranzaCollRef = collection(db, `${polizaPath}/cobranza`);
            const cobranzaSnap = await getDocs(cobranzaCollRef);

            const polizaDocRef = doc(db, polizaPath);

            if (cobranzaSnap.empty) {
                // Si la póliza ya no registra ninguna deuda viva en su cuenta corriente, apagamos el flag
                await updateDoc(polizaDocRef, { tieneDeuda: false });
            } else {
                // Si aún le quedan deudas activas de otros meses, nos aseguramos de que mantenga el flag
                await updateDoc(polizaDocRef, { tieneDeuda: true });
            }
        }

        // 4. Eliminar el documento maestro de la remesa
        await deleteDoc(remesaRef);

    } catch (error) {
        console.error("Error en deleteRemesaFull:", error);
        throw error;
    }
};

/**
 * 9. Borrado Profundo de Remesa de Liquidaciones
 */
export const deleteRemesaComisionFull = async (batchId, remesaDocId) => {
    try {
        const q = query(collection(db, 'public/data/liquidaciones'), where('batchId', '==', batchId));
        const snapshot = await getDocs(q);
        const docs = snapshot.docs;

        // Dividimos el lote total en segmentos seguros de 400 registros para no saturar el batch
        const chunkSize = 400;
        for (let i = 0; i < docs.length; i += chunkSize) {
            const chunk = docs.slice(i, i + chunkSize);
            const batch = writeBatch(db);
            chunk.forEach((d) => {
                batch.delete(d.ref);
            });
            await batch.commit();
        }

        // Una vez vaciada la colección de liquidaciones, eliminamos el documento maestro
        await deleteDoc(doc(db, 'public/data/remesas', remesaDocId));
    } catch (error) {
        console.error("Error en deleteRemesaComisionFull:", error);
        throw error;
    }
};

/**
 * 10. BORRADO TOTAL DE CLIENTE
 */
export const deleteClientFull = async (clientId) => {
    const clientRefPath = `public/data/clientes/${clientId}`;
    try {
        const polizasSnap = await getDocs(collection(db, clientRefPath, 'polizas'));
        for (const pDoc of polizasSnap.docs) {
            await deletePolizaFull(clientId, pDoc.id, pDoc.data().nPoliza);
        }

        const contratosSnap = await getDocs(collection(db, clientRefPath, 'contratos'));
        for (const cDoc of contratosSnap.docs) {
            await deleteContractFull(clientId, cDoc.id);
        }

        const directSubCols = ['cotizaciones_art', 'endosos', 'siniestros', 'tickets', 'deuda', 'gestiones_postventa'];
        for (const subCol of directSubCols) {
            await deleteCollectionInBatch(collection(db, clientRefPath, subCol));
        }

        await deleteDoc(doc(db, 'public/data/clientes', clientId));
    } catch (error) {
        console.error("Error en borrado total del cliente:", error);
        throw error;
    }
};


/**
 * 1. Rehabilitar Póliza Patrimonial
 * Cambia el estado a VIGENTE y remueve físicamente los rastros de anulación.
 * Sigue el estándar relacional y de persistencia del Canvas.
 */
export const rehabilitarPolizaFull = async (clientId, polizaId) => {
    if (!clientId || !polizaId) return;
    try {
        const path = `public/data/clientes/${clientId}/polizas`;
        
        const rawPayload = {
            estadoEmision: 'VIGENTE',
            motivoAnulacion: '',      // Al pasar vacíos o nulls, el cleaner 
            fechaAnulacion: '',       // los transforma en deleteField()
            primaAnulacion: '',       // de forma automática.
            quienGestiono: '',
            observacionesAnulacion: '',
            polizaReemplazo: '',
            despachoAnulacion: ''
        };

        // Al pasarle el polizaId como tercer parámetro, saveToFirestore sabe 
        // que es una edición, limpia el payload de raíz y lo actualiza físicamente.
        await saveToFirestore(path, rawPayload, polizaId);
        
        return rawPayload;
    } catch (error) {
        console.error("Error en rehabilitarPolizaFull con estándar cleanPayload:", error);
        throw error;
    }
};

/**
 * 2. Rehabilitar Contrato ART
 * Cambia el estado a Vigente y remueve físicamente los campos de anulación de la base de datos.
 * Sigue el estándar relacional y de persistencia del Canvas utilizando cleanPayload.
 */
export const rehabilitarContratoFull = async (clientId, contratoId) => {
    if (!clientId || !contratoId) return;
    try {
        const path = `public/data/clientes/${clientId}/contratos`;
        
        const rawPayload = {
            estadoPoliza: 'VIGENTE',
            motivoAnulacion: '',             // Al pasar vacíos, el cleaner centralizado
            fechaAnulacion: '',              // los transforma en deleteField() de forma
            quienGestionoAnulacion: '',      // automática para removerlos físicamente.
            observacionesAnulacion: ''
        };

        // Al pasar el contratoId como tercer parámetro, saveToFirestore sabe que es 
        // una edición, limpia el payload de raíz y ejecuta el updateDoc físicamente.
        await saveToFirestore(path, rawPayload, contratoId);
        
        return rawPayload;
    } catch (error) {
        console.error("Error en rehabilitarContratoFull con estándar cleanPayload:", error);
        throw error;
    }
};


/**
 * Función de respaldo para asegurar que los datos del cliente en una póliza
 * coincidan con los de su ficha maestra si se detecta una inconsistencia.
 */
export const repairPolicyClientData = async (policyRef, clientMasterData) => {
    const updateData = {
        clienteNombre: clientMasterData.nombre.toUpperCase(),
        clienteApellido: clientMasterData.persona === 'FISICA' ? clientMasterData.apellido.toUpperCase() : '',
        clienteDni: clientMasterData.dni || '',
        clienteCuit: clientMasterData.cuit || ''
    };
    await updateDoc(policyRef, updateData);
    return updateData;
};


/**
 * Limpieza profunda de PostVenta.
 * Remueve los campos de control de la raíz y elimina el historial de gestiones.
 */
export const deletePostVentaFull = async (clienteId, polizaId) => {
    const polizaRef = doc(db, `public/data/clientes/${clienteId}/polizas`, polizaId);
    const gestionesRef = collection(polizaRef, 'gestiones_postventa');

    try {
        const batch = writeBatch(db);

        // 1. Limpiamos los campos de la raíz
        batch.update(polizaRef, {
            postventa: "No",
            postventa_estado: deleteField(),
            postventa_quienGestiono: deleteField(),
            postventa_fechaProximaGestion: deleteField(),
            postventa_ultimoTipoGestion: deleteField(),
            postventa_etapa: deleteField(),
            postventa_numero_gestion: deleteField(),
            postventa_ultimaFechaGestion: deleteField(),
            postventa_numero_gestion: deleteField()
        });

        // 2. Eliminamos los documentos del historial (subcolección)
        const snapshot = await getDocs(gestionesRef);
        snapshot.forEach((d) => {
            batch.delete(d.ref);
        });

        await batch.commit();
        return true;
    } catch (error) {
        console.error("Error al eliminar PostVenta:", error);
        throw error;
    }
};


/**
 * Obtiene de forma segura y unificada el nombre del operador actual.
 * Consulta el perfil almacenado localmente en localStorage o el usuario autenticado de Firebase Auth,
 * devolviendo el resultado normalizado en mayúsculas y libre de espacios.
 * @returns {string} Nombre del operador en mayúsculas.
 */
export const getNombreOperador = () => {
    try {
        const stored = localStorage.getItem('userProfile');
        if (stored) {
            const parsed = JSON.parse(stored);
            const nombreSaneado = parsed.userName || parsed.nombre || parsed.displayName;
            if (nombreSaneado) {
                return nombreSaneado.toUpperCase().trim();
            }
        }
    } catch (e) {
        console.error("Error al extraer userProfile de localStorage:", e);
    }
    
    // Fallback: Nombre/Email del Auth de Firebase
    const fallbackName = auth.currentUser?.displayName || auth.currentUser?.email?.split('@')[0] || 'SISTEMA';
    return fallbackName.toUpperCase().trim();
};

/**
 * deleteCotizacionArtFull - Borrado de Cotización con desvinculación de Endoso ART
 * Borra la cotización, pero en lugar de eliminar el endoso, lo desvincula
 * y lo vuelve a setear en estado 'Pendiente' para permitir una nueva cotización.
 */
export const deleteCotizacionArtFull = async (cotizacion) => {
    if (!cotizacion || !cotizacion.id || !cotizacion.clienteId) {
        console.warn("Faltan identificadores para realizar el borrado de la cotización.");
        return;
    }
    
    try {
        const batch = writeBatch(db);
        
        // 1. Determinamos si es un cliente o prospecto para armar la ruta exacta de la cotización
        const isProspect = cotizacion.tipoEntidad === 'PROSPECTO';
        const quotePath = `public/data/${isProspect ? 'prospectos' : 'clientes'}/${cotizacion.clienteId}/cotizaciones_art/${cotizacion.id}`;
        const quoteRef = doc(db, quotePath);
        
        // Agregamos la eliminación de la cotización al lote
        batch.delete(quoteRef);

        // 2. Si la cotización proviene de un endoso de aumento de ART, lo desvinculamos de forma atómica
        if (
            cotizacion.tipoGestion === 'Aumento de Tarifa' && 
            cotizacion.endosoVinculadoId && 
            cotizacion.contratoOrigenId
        ) {
            const endosoPath = `public/data/clientes/${cotizacion.clienteId}/contratos/${cotizacion.contratoOrigenId}/aumentos_art/${cotizacion.endosoVinculadoId}`;
            const endosoRef = doc(db, endosoPath);
            
            // Actualizamos el endoso original para liberarlo
            batch.update(endosoRef, {
                estado: 'Pendiente',                     // Retorna a su estado original para poder recotizarse
                cotizacionVinculadaId: deleteField(),    // Removemos la clave vinculada de Firestore
                cotizacionVinculada: deleteField()       // Removemos el número de cotización de Firestore
            });
        }

        // Ejecutamos la transacción en lote de forma atómica
        await batch.commit();
    } catch (error) {
        console.error("Error en deleteCotizacionArtFull:", error);
        throw error;
    }
};

/**
 * 5. Borrado Profundo de Cuenta Corriente ART (Incluye historial de observaciones)
 * Sigue el estándar relacional de limpieza profunda para evitar colecciones huérfanas en Firestore.
 */
export const deleteCobranzaArtFull = async (contractPath, movimientoId) => {
    try {
        const pathCompleto = `${contractPath}/historial_cobranza/${movimientoId}`;
        // Limpiamos primero todas las observaciones vinculadas al hito contable
        await deleteCollectionInBatch(collection(db, pathCompleto, 'observaciones'));
        // Eliminamos el hito contable padre
        await deleteDoc(doc(db, `${contractPath}/historial_cobranza`, movimientoId));
    } catch (error) {
        console.error("Error en deleteCobranzaArtFull:", error);
        throw error;
    }
};

export const deleteRemesaAumentosFull = async (remesaDocId) => {
    try {
        const remesaRef = doc(db, 'public/data/remesas_aumentos', remesaDocId);
        const remesaSnap = await getDoc(remesaRef);

        if (!remesaSnap.exists()) {
            throw new Error("El lote de remesa seleccionado no existe en el sistema.");
        }

        const remesaData = remesaSnap.data();
        const paths = remesaData.documentosCreados || [];

        const chunkSize = 400;
        for (let i = 0; i < paths.length; i += chunkSize) {
            const chunk = paths.slice(i, i + chunkSize);
            const batch = writeBatch(db);
            
            chunk.forEach((path) => {
                batch.delete(doc(db, path));
            });
            
            await batch.commit();
        }

        // Eliminamos el documento maestro de control
        await deleteDoc(remesaRef);
    } catch (error) {
        console.error("Error en deleteRemesaAumentosFull:", error);
        throw error;
    }
};

export const deleteRemesaDeudaArtFull = async (remesaDocId) => {
    try {
        const remesaRef = doc(db, 'public/data/remesas_deuda_art', remesaDocId);
        const remesaSnap = await getDoc(remesaRef);

        if (!remesaSnap.exists()) {
            throw new Error("El lote de remesa seleccionado no existe en el sistema.");
        }

        const remesaData = remesaSnap.data();
        const paths = remesaData.documentosCreados || [];

        // Coleccionamos los contratos afectados de forma única para re-auditarlos al final
        const contratosAfectadosPaths = new Set();

        for (const path of paths) {
            const parts = path.split('/');
            // public/data/clientes/{clientId}/contratos/{contratoId}/historial_cobranza/{movimientoId}
            if (parts.length >= 8) {
                const clientId = parts[3];
                const contratoId = parts[5];
                const movimientoId = parts[7];

                const contractPath = `public/data/clientes/${clientId}/contratos/${contratoId}`;
                contratosAfectadosPaths.add(contractPath);

                // Borramos recursivamente observaciones y el hito contable
                await deleteCobranzaArtFull(contractPath, movimientoId);
            }
        }

        // Re-escaneo de saldos secuenciales para cada contrato afectado
        for (const parentPath of Array.from(contratosAfectadosPaths)) {
            const subRef = collection(db, `${parentPath}/historial_cobranza`);
            const snapSub = await getDocs(subRef);
            const remainingMovs = snapSub.docs.map(d => ({ id: d.id, ...d.data() }));

            const contractRef = doc(db, parentPath);

            if (remainingMovs.length === 0) {
                await updateDoc(contractRef, {
                    artDeudaGlobal: deleteField(),
                    tieneDeuda: deleteField(),
                    artEstadoContrato: deleteField(),
                    artEstadoGestion: deleteField(),
                    artEstadoDeuda: deleteField(),
                    artUltimoMovimientoTipo: deleteField(),
                    artUltimoMovimientoMonto: deleteField(),
                    artUltimoMovimientoFecha: deleteField(),
                    artUltimoMovimientoResponsable: deleteField(),
                    artUltimaActualizacion: serverTimestamp()
                });
            } else {
                // Re-ordenamiento secuencial para encontrar el último movimiento activo real
                remainingMovs.sort((a, b) => {
                    const dateA = a.fechaMovimiento || '';
                    const dateB = b.fechaMovimiento || '';
                    if (dateA !== dateB) return dateB.localeCompare(dateA);
                    
                    const timeA = a.fechaCarga?.seconds || (a.fechaCarga instanceof Date ? a.fechaCarga.getTime() / 1000 : Date.now() / 1000);
                    const timeB = b.fechaCarga?.seconds || (b.fechaCarga instanceof Date ? b.fechaCarga.getTime() / 1000 : Date.now() / 1000);
                    return timeB - timeA;
                });

                const latestMov = remainingMovs[0];
                const finalDeudaValue = latestMov.saldoResultante || 0;

                await updateDoc(contractRef, {
                    artDeudaGlobal: finalDeudaValue,
                    tieneDeuda: finalDeudaValue > 0,
                    artEstadoContrato: latestMov.estadoContrato || 'Sin Definir',
                    artEstadoGestion: latestMov.estadoGestion || 'Sin Asignar',
                    artEstadoDeuda: finalDeudaValue > 0 ? (latestMov.estadoDeuda || 'Con Deuda') : 'Al día',
                    artUltimoMovimientoTipo: latestMov.tipoMovimiento || '',
                    artUltimoMovimientoMonto: latestMov.importe || 0,
                    artUltimoMovimientoFecha: latestMov.fechaMovimiento || '',
                    artUltimoMovimientoResponsable: latestMov.quienInformo || latestMov.quienCobro || 'OPERADOR',
                    artUltimaActualizacion: serverTimestamp()
                });
            }
        }

        // Eliminamos el documento de la remesa maestra de deudas de ART
        await deleteDoc(remesaRef);

    } catch (error) {
        console.error("Error en deleteRemesaDeudaArtFull:", error);
        throw error;
    }
};


/**
 * Borrado Profundo de Cliente Calificado / Oportunidad Cross-Selling
 * Elimina la subcolección interna 'observaciones' de forma atómica
 * y posteriormente elimina el registro padre para evitar datos huérfanos.
 */
export const deleteClienteCrosellingFull = async (crosellingId) => {
    if (!crosellingId) return;
    const itemRefPath = `public/data/clientes_croselling/${crosellingId}`;
    try {
        // 1. Limpiamos primero todas las observaciones vinculadas (subcolección)
        await deleteCollectionInBatch(collection(db, itemRefPath, 'observaciones'));
        
        // 2. Eliminamos el documento padre
        await deleteDoc(doc(db, 'public/data/clientes_croselling', crosellingId));
    } catch (error) {
        console.error("Error en deleteClienteCrosellingFull:", error);
        throw error;
    }
};